import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import heartbeatCampaignService from "services/heartbeatCampaignService";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import {
  firstErrorTab,
  isQuestionErrorKey,
  questionOptionsErrorKey,
  questionTextErrorKey,
  validateCampaign,
} from "./validation";

const INVALID_SELECTOR = '[aria-invalid="true"], [data-invalid-focus="true"]';

/**
 * State, loaders, handlers and save logic for the Heartbeat campaign editor.
 */
const useCampaignEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const isEdit = !!id;
  const qrRef = useRef(null);
  const formRef = useRef(null);
  const originalStatusRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [responseCount, setResponseCount] = useState(0);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusRequest, setFocusRequest] = useState(0);

  // Recipient selection state
  const [potentialRecipients, setPotentialRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientsError, setRecipientsError] = useState(null);
  const [filterRecipientCount, setFilterRecipientCount] = useState(null);
  const [recipientCountCache, setRecipientCountCache] = useState(null);

  // AI question generation state
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiCampaignType, setAiCampaignType] = useState("standard");
  const [aiFocusAreas, setAiFocusAreas] = useState(["faith_journey", "community", "life_challenges"]);
  const [showAiGenerator, setShowAiGenerator] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Status confirmation modal state
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "draft",
    deliveryMethod: "email",
    emailSubject: "We'd love to hear from you!",
    emailBody: "Thank you for visiting! We'd love to learn more about you.",
    smsMessage: "Thanks for visiting! Please take a moment to fill out our quick survey: {{survey_link}}",
    questions: [],
    recipientMode: "automatic",
    triggerType: "member_creation",
    triggerConfig: {},
    filterCriteria: null,
    deliveryWindowStart: 9,
    deliveryWindowEnd: 20,
    organizationTimezone: "America/Chicago",
    scheduleDays: null,
    welcomeMessage: "",
    surveyDescription: "",
    surveyType: "spiritual",
    surveyTopic: "",
    surveyGoals: [],
    enablePrayerRequests: false,
  });

  useEffect(() => {
    if (isEdit) {
      fetchCampaign();
    } else {
      loadDefaultQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch potential recipients when the recipients tab is selected
  useEffect(() => {
    if (activeTab === "recipients") {
      fetchPotentialRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load templates when questions tab is opened
  useEffect(() => {
    if (activeTab === "questions") {
      fetchTemplates();
    }
  }, [activeTab]);

  // After a failed save, focus the first invalid field on the tab that was switched to
  useEffect(() => {
    if (!focusRequest) return;
    formRef.current?.querySelector(INVALID_SELECTOR)?.focus();
  }, [focusRequest]);

  const clearErrors = (predicate) => {
    setErrors((prev) => {
      const keys = Object.keys(prev).filter(predicate);
      if (keys.length === 0) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };
  const clearError = (key) => clearErrors((k) => k === key);
  const clearQuestionErrors = () => clearErrors(isQuestionErrorKey);

  // Programmatic tab change: keep the ?tab= param in sync with the Tabs component
  const selectTab = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tabId);
        return next;
      },
      { replace: true }
    );
  };

  const fetchTemplates = async () => {
    try {
      const response = await heartbeatCampaignService.getQuestionTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const closeSaveTemplateModal = () => {
    setShowSaveTemplateModal(false);
    setTemplateName("");
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      notify.error("Enter a template name");
      return;
    }

    try {
      setSavingTemplate(true);
      const response = await heartbeatCampaignService.saveQuestionTemplate({
        name: templateName,
        questions: formData.questions,
        campaignType: aiCampaignType,
        focusAreas: aiFocusAreas,
        isAiGenerated: true,
      });

      if (response.success) {
        notify.success("Template saved");
        setShowSaveTemplateModal(false);
        setTemplateName("");
        fetchTemplates();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save template"));
    } finally {
      setSavingTemplate(false);
    }
  };

  const loadTemplate = (template) => {
    const questionsWithIds = template.questions.map((q, i) => ({
      ...q,
      id: Date.now() + i,
    }));
    setFormData((prev) => ({
      ...prev,
      questions: questionsWithIds,
    }));
    clearQuestionErrors();
    notify.success(`Loaded "${template.name}" template`);
  };

  const fetchPotentialRecipients = async () => {
    try {
      setLoadingRecipients(true);
      setRecipientsError(null);
      const response = await AtriumApi.get("/v2/heartbeat/campaigns/potential-recipients", {
        params: {
          limit: 200,
        },
      });

      if (response.data?.success && response.data?.data) {
        setPotentialRecipients(response.data.data);
      }
    } catch (error) {
      setRecipientsError(error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const toggleRecipient = (userId) => {
    setSelectedRecipients((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const filteredRecipients = useMemo(() => {
    const searchLower = recipientSearch.toLowerCase();
    return potentialRecipients.filter((r) => {
      const fullName = `${r.firstName || ""} ${r.lastName || ""}`.toLowerCase();
      const email = (r.email || "").toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [potentialRecipients, recipientSearch]);

  const selectAllRecipients = () => {
    setSelectedRecipients(filteredRecipients.map((r) => r.id));
  };

  const deselectAllRecipients = () => {
    setSelectedRecipients([]);
  };

  const loadDefaultQuestions = async () => {
    try {
      const response = await heartbeatCampaignService.getDefaultQuestions();
      if (response.success && response.data) {
        setFormData((prev) => ({
          ...prev,
          questions: response.data,
        }));
      }
    } catch (error) {
      console.error("Error loading default questions:", error);
      setFormData((prev) => ({
        ...prev,
        questions: [
          {
            id: Date.now(),
            type: "single_choice",
            text: "How did you hear about us?",
            required: true,
            options: ["Friend/Family", "Social Media", "Search Engine", "Drove By", "Other"],
          },
          {
            id: Date.now() + 1,
            type: "single_choice",
            text: "Is this your first time visiting?",
            required: true,
            options: ["Yes", "No"],
          },
          {
            id: Date.now() + 2,
            type: "multiple_choice",
            text: "What are you interested in learning more about?",
            required: false,
            options: ["Small Groups", "Youth Programs", "Children's Ministry", "Volunteer Opportunities", "Worship Team"],
          },
        ],
      }));
    }
  };

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [campaignResponse, statsResponse] = await Promise.all([
        heartbeatCampaignService.getCampaignById(id),
        heartbeatCampaignService.getCampaignStats(id).catch(() => ({ success: false })),
      ]);

      if (campaignResponse.success) {
        originalStatusRef.current = campaignResponse.data.status;
        setFormData({
          name: campaignResponse.data.name,
          description: campaignResponse.data.description || "",
          status: campaignResponse.data.status,
          deliveryMethod: campaignResponse.data.deliveryMethod,
          emailSubject: campaignResponse.data.emailSubject || "",
          emailBody: campaignResponse.data.emailBody || "",
          smsMessage: campaignResponse.data.smsMessage || "",
          questions: campaignResponse.data.questions || [],
          recipientMode: campaignResponse.data.recipientMode || "automatic",
          triggerType: campaignResponse.data.triggerType || "member_creation",
          triggerConfig: campaignResponse.data.triggerConfig || {},
          filterCriteria: campaignResponse.data.filterCriteria || null,
          deliveryWindowStart: campaignResponse.data.deliveryWindowStart ?? 9,
          deliveryWindowEnd: campaignResponse.data.deliveryWindowEnd ?? 20,
          organizationTimezone: campaignResponse.data.organizationTimezone || "America/Chicago",
          scheduleDays: campaignResponse.data.scheduleDays ?? null,
          isSystem: campaignResponse.data.isSystem || false,
          isDeletable: campaignResponse.data.isDeletable !== false,
          qrCodeToken: campaignResponse.data.qrCodeToken || null,
          qrCodeUrl: campaignResponse.data.qrCodeUrl || null,
          qrCodeShortUrl: campaignResponse.data.qrCodeShortUrl || null,
          welcomeMessage: campaignResponse.data.welcomeMessage || "",
          surveyDescription: campaignResponse.data.surveyDescription || "",
          surveyType: campaignResponse.data.surveyType || "spiritual",
          surveyTopic: campaignResponse.data.surveyTopic || "",
          surveyGoals: campaignResponse.data.surveyGoals || [],
          enablePrayerRequests: campaignResponse.data.enablePrayerRequests || false,
        });
        setRecipientCountCache(campaignResponse.data.recipientCountCache ?? null);
        // Restore manually selected recipients from saved filterCriteria
        const savedManualIds = campaignResponse.data.filterCriteria?.manualUserIds;
        if (savedManualIds?.length > 0) {
          setSelectedRecipients(savedManualIds);
        }
      }

      if (statsResponse.success && statsResponse.data) {
        setResponseCount(statsResponse.data.totalResponses || 0);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      type: "text",
      text: "",
      required: false,
      options: [],
    };
    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
    clearError("questions");
  };

  const updateQuestion = (index, field, value) => {
    setFormData((prev) => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
    if (field === "text") clearError(questionTextErrorKey(index));
    if (field === "type") clearError(questionOptionsErrorKey(index));
  };

  const removeQuestion = (index) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
    clearQuestionErrors();
  };

  const moveQuestion = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formData.questions.length) return;
    setFormData((prev) => {
      const questions = [...prev.questions];
      const [removed] = questions.splice(fromIndex, 1);
      questions.splice(toIndex, 0, removed);
      return { ...prev, questions };
    });
    clearQuestionErrors();
  };

  const addOption = (questionIndex) => {
    setFormData((prev) => {
      const questions = [...prev.questions];
      questions[questionIndex] = {
        ...questions[questionIndex],
        options: [...(questions[questionIndex].options || []), ""],
      };
      return { ...prev, questions };
    });
    clearError(questionOptionsErrorKey(questionIndex));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setFormData((prev) => {
      const questions = [...prev.questions];
      const options = [...questions[questionIndex].options];
      options[optionIndex] = value;
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...prev, questions };
    });
    clearError(questionOptionsErrorKey(questionIndex));
  };

  const removeOption = (questionIndex, optionIndex) => {
    setFormData((prev) => {
      const questions = [...prev.questions];
      questions[questionIndex] = {
        ...questions[questionIndex],
        options: questions[questionIndex].options.filter((_, i) => i !== optionIndex),
      };
      return { ...prev, questions };
    });
    clearError(questionOptionsErrorKey(questionIndex));
  };

  // Perform the actual save operation
  const performSave = async (statusOverride = null) => {
    try {
      setSaving(true);
      const finalStatus = statusOverride || formData.status;
      // Convert status to isActive for backend
      const payload = {
        ...formData,
        status: finalStatus,
        isActive: finalStatus === "active",
        emailTemplate: formData.emailBody,
        smsTemplate: formData.smsMessage,
        recipientMode: formData.recipientMode,
        triggerType: formData.triggerType,
        triggerConfig: formData.triggerConfig,
        filterCriteria: {
          ...(formData.filterCriteria || {}),
          manualUserIds: selectedRecipients.length > 0 ? selectedRecipients : undefined,
        },
        deliveryWindowStart: formData.deliveryWindowStart,
        deliveryWindowEnd: formData.deliveryWindowEnd,
        organizationTimezone: formData.organizationTimezone,
        scheduleDays: formData.scheduleDays,
      };

      if (isEdit) {
        await heartbeatCampaignService.updateCampaign(id, payload);
        track(EVENTS.HEARTBEAT_CAMPAIGN_UPDATED, { campaign_id: id, campaign_name: formData.name });
        notify.success("Campaign saved");
      } else {
        await heartbeatCampaignService.createCampaign(payload);
        track(EVENTS.HEARTBEAT_CAMPAIGN_CREATED, { campaign_name: formData.name });
        notify.success("Campaign created");
      }
      navigate("/admin/heartbeat");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save campaign"));
    } finally {
      setSaving(false);
      setShowStatusConfirmModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateCampaign(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      selectTab(firstErrorTab(nextErrors));
      setFocusRequest((count) => count + 1);
      notify.error("Fix the highlighted fields");
      return;
    }

    // Editing an active campaign back to draft stops sends, so confirm it
    if (isEdit && originalStatusRef.current === "active" && formData.status === "draft") {
      const confirmed = await confirm({
        title: `Deactivate "${formData.name}"?`,
        message: "This campaign is active. Setting it back to draft stops it from sending.",
        confirmLabel: "Save as Draft",
        tone: "danger",
      });
      if (!confirmed) return;
    }

    // Show confirmation modal if status is draft (for new campaigns only)
    if (!isEdit && formData.status === "draft") {
      setShowStatusConfirmModal(true);
      return;
    }

    await performSave();
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: `Delete "${formData.name}"?`,
      message: "This campaign will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      setDeleting(true);
      await heartbeatCampaignService.deleteCampaign(id);
      notify.success("Campaign deleted");
      navigate("/admin/heartbeat");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete campaign"));
      setDeleting(false);
    }
  };

  const handleSendNow = async () => {
    const confirmed = await confirm({
      title: `Send "${formData.name}" now?`,
      message: "The survey goes to all eligible recipients immediately. This cannot be undone.",
      confirmLabel: "Send Now",
    });
    if (!confirmed) return;
    try {
      setSendingNow(true);
      const response = await heartbeatCampaignService.sendNow(id, formData.filterCriteria || null, formData.deliveryMethod || null);
      if (response.success) {
        notify.success(response.message || "Survey sent");
        track(EVENTS.HEARTBEAT_SURVEY_SENT, { campaign_id: id });
      } else {
        notify.error(response.error || "Could not send survey");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not send survey"));
    } finally {
      setSendingNow(false);
    }
  };

  const handlePreviewSurvey = async () => {
    try {
      setGeneratingPreview(true);
      const response = await heartbeatCampaignService.generatePreview(id);
      if (response.success && response.data?.previewUrl) {
        window.open(response.data.previewUrl, "_blank");
      } else {
        notify.error("Could not generate preview link");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not generate preview"));
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleGenerateAiQuestions = async () => {
    if (formData.questions.length > 0) {
      const confirmed = await confirm({
        title: "Replace Questions?",
        message: `This replaces your current ${formData.questions.length} question(s) with generated questions.`,
        confirmLabel: "Replace Questions",
      });
      if (!confirmed) return;
    }
    // Validate based on survey type
    if (formData.surveyType === "spiritual") {
      if (aiFocusAreas.length === 0) {
        notify.error("Select at least one focus area");
        return;
      }
    } else if (!formData.surveyTopic?.trim()) {
      notify.error("Enter a survey topic");
      return;
    }

    try {
      setGeneratingAi(true);

      const params = formData.surveyType === "spiritual"
        ? {
            campaignType: aiCampaignType,
            focusAreas: aiFocusAreas,
            surveyType: "spiritual",
          }
        : {
            campaignType: aiCampaignType,
            surveyType: "general",
            surveyTopic: formData.surveyTopic,
            surveyGoals: formData.surveyGoals,
          };

      const response = await heartbeatCampaignService.generateAiQuestions(id || "new", params);

      if (response.success && response.data?.questions) {
        const questionsWithIds = response.data.questions.map((q, i) => ({
          ...q,
          id: Date.now() + i,
        }));

        setFormData((prev) => ({
          ...prev,
          questions: questionsWithIds,
        }));
        clearQuestionErrors();

        notify.success(`Generated ${questionsWithIds.length} questions`);
        setShowAiGenerator(false);
      } else {
        notify.error("Could not generate questions");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not generate questions"));
    } finally {
      setGeneratingAi(false);
    }
  };

  const toggleFocusArea = (area) => {
    setAiFocusAreas((prev) => {
      if (prev.includes(area)) {
        return prev.filter((a) => a !== area);
      }
      return [...prev, area];
    });
  };

  const loadSliderQuestions = async () => {
    try {
      const response = await heartbeatCampaignService.getDefaultSliderQuestions();
      if (response.success && response.data) {
        const questionsWithIds = response.data.map((q, i) => ({
          ...q,
          id: Date.now() + i,
        }));
        setFormData((prev) => ({
          ...prev,
          questions: questionsWithIds,
        }));
        clearQuestionErrors();
        notify.success("Loaded default slider questions");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not load default slider questions"));
    }
  };

  const handleGenerateQrCode = async () => {
    if (!isEdit) {
      notify.error("Save the campaign before generating a QR code");
      return;
    }

    try {
      setGeneratingQr(true);
      const response = await heartbeatCampaignService.generateQrCode(id, false);
      if (response.success) {
        setFormData((prev) => ({
          ...prev,
          qrCodeToken: response.data.qrCodeToken,
          qrCodeUrl: response.data.qrCodeUrl,
          qrCodeShortUrl: response.data.qrCodeShortUrl,
        }));
        notify.success("QR code generated");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not generate QR code"));
    } finally {
      setGeneratingQr(false);
    }
  };

  const copyToClipboard = (text, label = "URL") => {
    navigator.clipboard.writeText(text);
    notify.success(`${label} copied`);
  };

  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = 256;
    canvas.height = 256;

    img.onload = () => {
      // Opaque white background so the exported PNG scans on any surface
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, 256, 256);
      ctx.drawImage(img, 0, 0, 256, 256);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formData.name.replace(/\s+/g, "-").toLowerCase()}-qr-code.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        notify.success("QR code downloaded");
      }, "image/png");
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return {
    id,
    isEdit,
    formRef,
    qrRef,
    formData,
    errors,
    handleChange,

    loading,
    loadError,
    fetchCampaign,
    saving,
    deleting,
    responseCount,
    activeTab,
    setActiveTab,

    handleSubmit,
    performSave,
    handleDelete,
    showStatusConfirmModal,
    setShowStatusConfirmModal,

    // Questions
    addQuestion,
    updateQuestion,
    removeQuestion,
    moveQuestion,
    addOption,
    updateOption,
    removeOption,
    templates,
    loadTemplate,
    loadSliderQuestions,
    showSaveTemplateModal,
    setShowSaveTemplateModal,
    closeSaveTemplateModal,
    templateName,
    setTemplateName,
    savingTemplate,
    handleSaveTemplate,
    generatingAi,
    aiCampaignType,
    setAiCampaignType,
    aiFocusAreas,
    toggleFocusArea,
    showAiGenerator,
    setShowAiGenerator,
    handleGenerateAiQuestions,

    // Recipients
    potentialRecipients,
    filteredRecipients,
    selectedRecipients,
    toggleRecipient,
    selectAllRecipients,
    deselectAllRecipients,
    recipientSearch,
    setRecipientSearch,
    loadingRecipients,
    recipientsError,
    fetchPotentialRecipients,
    filterRecipientCount,
    setFilterRecipientCount,
    recipientCountCache,

    // Delivery
    generatingQr,
    handleGenerateQrCode,
    copyToClipboard,
    downloadQRCode,
    generatingPreview,
    handlePreviewSurvey,
    sendingNow,
    handleSendNow,
  };
};

export default useCampaignEditor;
